import { motion } from "framer-motion";
import { Paintbrush, Palette, Wrench, Droplets, Sparkles, ArrowRight, Lightbulb, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Main 6 services for the grid
const SERVICES = [
  {
    icon: Paintbrush,
    title: "Full Resprays",
    formValue: "full_respray",
    desc: "Complete colour changes and factory-quality refinishing for any vehicle.",
  },
  {
    icon: Palette,
    title: "Custom Paint",
    formValue: "custom_paint",
    desc: "Candy colours, pearls, metallics, and custom designs that set your ride apart.",
  },
  {
    icon: Wrench,
    title: "Panel & Dent Repair",
    formValue: "dent_repair",
    desc: "Expert bodywork from minor scratches to full panel restoration.",
  },
  {
    icon: Droplets,
    title: "Ceramic Coating",
    formValue: "ceramic_coating",
    desc: "Long-lasting protection that repels water, dirt, and UV damage.",
  },
  {
    icon: Sparkles,
    title: "Detailing",
    formValue: "detailing",
    desc: "Professional detailing to keep your vehicle looking its absolute best.",
  },
  {
    icon: Wrench,
    title: "Bumper Repair",
    formValue: "bumper_repair",
    desc: "Cracked, scraped or damaged bumpers repaired to factory standard.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

export const ServicesSection = () => {
  const navigate = useNavigate();

  const handleServiceClick = (formValue) => {
    window.dispatchEvent(new CustomEvent('selectService', { detail: formValue }));
    navigate('/quote');
  };

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div {...fadeUp} className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-[1px] bg-[#3b82f6]" />
            <span className="text-sm tracking-[0.3em] uppercase text-zinc-500 font-body font-medium">What We Do</span>
          </div>
          <h2 data-testid="services-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white leading-[0.95]">
            Our Services
          </h2>
        </motion.div>

        {/* Services Grid - Cleaner bento style */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((service, i) => {
            const Icon = service.icon;
            return (
              <motion.button
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                onClick={() => handleServiceClick(service.formValue)}
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group bg-[#0a0a0a] border border-white/5 p-6 md:p-8 cursor-pointer text-left w-full hover:border-[#3b82f6]/30 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:border-[#3b82f6]/50 group-hover:bg-[#3b82f6]/10 transition-all duration-300">
                    <Icon size={20} strokeWidth={1.5} className="text-zinc-400 group-hover:text-[#3b82f6] transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg uppercase tracking-wide text-white group-hover:text-[#3b82f6] transition-colors mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-zinc-600 font-body leading-relaxed group-hover:text-zinc-500 transition-colors">
                      {service.desc}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/5 text-xs tracking-[0.15em] uppercase text-zinc-700 group-hover:text-[#3b82f6] transition-colors">
                  Get a Quote <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Premium Services Banner */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-8 grid md:grid-cols-2 gap-4"
        >
          {/* Mobile Inspection */}
          <button
            onClick={() => navigate('/booking/mobile-inspection')}
            data-testid="mobile-inspection-cta"
            className="group bg-[#0a0a0a] border border-[#3b82f6]/20 p-6 text-left cursor-pointer hover:border-[#3b82f6]/50 transition-all"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#3b82f6]/10 border border-[#3b82f6]/30 flex items-center justify-center flex-shrink-0">
                <Truck size={20} className="text-[#3b82f6]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-lg uppercase tracking-wide text-white">Mobile Quote Service</h3>
                  <span className="text-[10px] px-2 py-0.5 bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30 font-body uppercase tracking-wider">New</span>
                </div>
                <p className="text-sm text-zinc-500 font-body mb-3">
                  We come to you! Professional on-site inspection anywhere in Brisbane metro.
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-xl text-[#3b82f6]">$150</span>
                  <span className="text-xs text-[#22c55e] font-body">Credited towards full resprays</span>
                </div>
              </div>
            </div>
          </button>

          {/* Headlight Restoration */}
          <button
            onClick={() => navigate('/booking/headlight')}
            data-testid="headlight-restoration-cta"
            className="group bg-[#0a0a0a] border border-[#f59e0b]/20 p-6 text-left cursor-pointer hover:border-[#f59e0b]/50 transition-all"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#f59e0b]/10 border border-[#f59e0b]/30 flex items-center justify-center flex-shrink-0">
                <Lightbulb size={20} className="text-[#f59e0b]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-lg uppercase tracking-wide text-white">Headlight Restoration</h3>
                  <span className="text-[10px] px-2 py-0.5 bg-[#3b82f6]/20 text-[#3b82f6] border border-[#3b82f6]/30 font-body uppercase tracking-wider">Book Online</span>
                </div>
                <p className="text-sm text-zinc-500 font-body mb-3">
                  Crystal clear results. Professional wet-sanding and UV protective clear coat.
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-xl text-white">$250<span className="text-sm text-zinc-600">/pair</span></span>
                  <span className="text-xs text-[#3b82f6] font-body">$50 deposit to book</span>
                </div>
              </div>
            </div>
          </button>
        </motion.div>

        {/* Bottom tag */}
        <motion.div 
          initial={{ opacity: 0 }} 
          whileInView={{ opacity: 1 }} 
          viewport={{ once: true }} 
          transition={{ delay: 0.5 }} 
          className="mt-10 flex items-center justify-center gap-3"
        >
          <div className="w-2 h-2 bg-[#3b82f6]" />
          <p className="text-xs tracking-[0.2em] uppercase text-zinc-600 font-body">
            JDM specialists / all vehicle types welcome
          </p>
          <div className="w-2 h-2 bg-[#3b82f6]" />
        </motion.div>
      </div>
    </section>
  );
};
